<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';

const props = withDefaults(defineProps<{
	modelValue?: string;
	error?: boolean;
	length?: number;
}>(), {
	modelValue: '',
	error: false,
	length: 6,
});

const emit = defineEmits<{
	'update:modelValue': [value: string];
}>();

const inputs = ref<HTMLInputElement[]>([]);
const digits = ref<string[]>(Array(props.length).fill(''));

const setInputRef = (el: any, index: number) => {
	if (el) inputs.value[index] = el;
};

const focusInput = (index: number) => {
	nextTick(() => {
		inputs.value[index]?.focus();
	});
};

const onInput = (index: number, event: Event) => {
	const target = event.target as HTMLInputElement;
	const value = target.value.replace(/\D/g, '');

	if (value.length > 1) {
		// Handle paste into single input
		const chars = value.slice(0, props.length - index).split('');
		chars.forEach((char, i) => {
			if (index + i < props.length) {
				digits.value[index + i] = char;
			}
		});
		const nextIndex = Math.min(index + chars.length, props.length - 1);
		focusInput(nextIndex);
	} else {
		digits.value[index] = value;
		if (value && index < props.length - 1) {
			focusInput(index + 1);
		}
	}

	emit('update:modelValue', digits.value.join(''));
};

const onKeydown = (index: number, event: KeyboardEvent) => {
	if (event.key === 'Backspace') {
		if (!digits.value[index] && index > 0) {
			digits.value[index - 1] = '';
			focusInput(index - 1);
			emit('update:modelValue', digits.value.join(''));
			event.preventDefault();
		}
	}
	if (event.key === 'ArrowLeft' && index > 0) {
		focusInput(index - 1);
		event.preventDefault();
	}
	if (event.key === 'ArrowRight' && index < props.length - 1) {
		focusInput(index + 1);
		event.preventDefault();
	}
};

const onPaste = (event: ClipboardEvent) => {
	event.preventDefault();
	const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '');
	const chars = pasted.slice(0, props.length).split('');

	chars.forEach((char, i) => {
		digits.value[i] = char;
	});

	const nextIndex = Math.min(chars.length, props.length - 1);
	focusInput(nextIndex);
	emit('update:modelValue', digits.value.join(''));
};

const hasValue = (index: number) => !!digits.value[index];

watch(() => props.modelValue, (val) => {
	const chars = (val || '').split('');
	digits.value = Array(props.length).fill('').map((_, i) => chars[i] || '');
});

const focus = () => {
	const firstEmpty = digits.value.findIndex(d => !d);
	focusInput(firstEmpty >= 0 ? firstEmpty : props.length - 1);
};

defineExpose({ focus });
</script>

<template>
	<div
		class="form-otp-input"
		:class="{ 'form-otp-input--error': error }"
	>
		<div
			v-for="(_, index) in length"
			:key="index"
			class="form-otp-input__cell"
			:class="{
				'form-otp-input__cell--filled': hasValue(index),
				'form-otp-input__cell--error': error,
			}"
		>
			<input
				:ref="(el) => setInputRef(el, index)"
				type="text"
				inputmode="numeric"
				pattern="[0-9]*"
				maxlength="2"
				autocomplete="one-time-code"
				class="form-otp-input__input"
				:value="digits[index]"
				@input="onInput(index, $event)"
				@keydown="onKeydown(index, $event)"
				@paste="onPaste"
			/>
		</div>
	</div>
</template>

<style lang="scss">
.form-otp-input {
	display: flex;
	gap: 1rem;

	--otp-height: 5.2rem;

	@include min-desktop {
		--otp-height: 6.2rem;
	}

	&__cell {
		flex: 1;
		position: relative;
		border-radius: 1.6rem;
		border: 1px solid rgba(#000, 0.12);
		background: transparent;
		transition: border-color ease 0.2s;
		overflow: hidden;

		@include min-desktop {
			flex: 0 0 7.2rem;
		}

		@include theme-dark {
			border-color: rgba(var(--color-active-rgb), 0.25);
		}

		&--filled {
			border-color: var(--color-purple);

			@include theme-dark {
				border-color: var(--color-active);
			}
		}

		&--error {
			border: 1px solid #F75E60;

			@include theme-dark {
				border-color: #FBAFB0;
			}
		}
	}

	&__input {
		width: 100%;
		height: var(--otp-height);
		text-align: center;
		border: none;
		background: transparent;
		color: var(--color-text);
		@include font-16-pro-regular;
		outline: none;

		&::placeholder {
			color: var(--color-text);
			opacity: 0.5;
		}
	}
}
</style>
